import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, ChevronRight, AlertTriangle, HelpCircle, Users } from "lucide-react";
import api, { fmtTime, fmtDay } from "../../lib/api";
import { Loading, StatusPill } from "../../components/Shared";

export default function CMToday() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/cm/today").then((r) => setD(r.data)).catch(() => setD({})); }, []);
  if (!d) return <Loading label="Loading your day…" />;
  const cm = d.care_manager;
  return (
    <div className="p1-animate-in space-y-5" data-testid="cm-today">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">{fmtDay(new Date().toISOString())}</p>
        <h1 className="font-heading text-2xl font-bold text-stone-900">Namaste, {cm?.name?.split(" ")[0]}</h1>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-stone-500"><MapPin className="h-4 w-4" /> {(d.zones || []).join(" · ") || "Jaipur"} · {d.families_count} families assigned</p>
      </div>

      {d.urgent_incidents?.length > 0 && (
        <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-4" data-testid="cm-urgent-incidents">
          <p className="flex items-center gap-2 font-heading font-bold text-red-800"><AlertTriangle className="h-5 w-5" /> Urgent issues</p>
          <ul className="mt-2 space-y-1 text-sm text-red-800">
            {d.urgent_incidents.map((i) => <li key={i.id}>• {i.category.replace(/_/g, " ")} — {i.description?.slice(0, 80)}</li>)}
          </ul>
        </div>
      )}

      <section>
        <h2 className="font-heading text-lg font-semibold text-stone-900">Scheduled visits</h2>
        <div className="mt-3 space-y-3">
          {(d.visits || []).length === 0 && <p className="rounded-2xl border border-dashed border-stone-300 p-5 text-center text-sm text-stone-400" data-testid="cm-no-visits">No upcoming visits. Enjoy the calm.</p>}
          {(d.visits || []).map((v) => (
            <Link key={v.id} to={`/app/cm/visit/${v.id}`} className="flex items-center justify-between gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md" data-testid={`cm-visit-${v.id}`}>
              <div>
                <p className="font-heading font-semibold text-stone-900">{v.parent?.name || "Parent"}</p>
                <p className="text-sm text-stone-500">{v.purpose}</p>
                <p className="mt-1 flex items-center gap-1 text-xs text-stone-400"><Clock className="h-3.5 w-3.5" /> {fmtDay(v.scheduled_at)} · {fmtTime(v.scheduled_at)}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <StatusPill status={v.status} />
                <ChevronRight className="h-4 w-4 text-stone-300" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section id="families">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><Users className="h-5 w-5 text-emerald-800" /> Open service requests</h2>
        <div className="mt-3 space-y-2">
          {(d.open_requests || []).length === 0 && <p className="text-sm text-stone-400">None right now.</p>}
          {(d.open_requests || []).map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-2xl border border-stone-200 bg-white p-4" data-testid={`cm-request-${r.id}`}>
              <div><p className="text-sm font-semibold text-stone-800">{r.type}</p><p className="text-xs text-stone-500">{r.details}</p></div>
              <StatusPill status={r.status} />
            </div>
          ))}
        </div>
      </section>

      <p className="flex items-start gap-2 rounded-2xl bg-stone-100 p-4 text-xs text-stone-500">
        <HelpCircle className="mt-0.5 h-4 w-4 shrink-0" /> You only see families assigned to you. All visits, reports and incidents are timestamped and visible to the family.
      </p>
    </div>
  );
}
